
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { InsightCard as InsightCardType } from "@/types/insight";
import { useState, useEffect, useCallback } from "react";
import { useLinkedPolls } from "@/hooks/insights/useLinkedPolls";
import LinkedPollsSection from "./LinkedPollsSection";
import CardHeader from "./CardHeader";
import AgreementBar from "./AgreementBar";
import ShareButton from "@/components/social/ShareButton";
import { useNavigate } from "react-router-dom";
import { useVotePermissions } from "@/hooks/polls/useVotePermissions";
import { Poll } from "@/types/poll";
import LikeButton from "@/components/social/LikeButton";
import TrendingBadge from "@/components/social/TrendingBadge";
import { MessageSquare } from "lucide-react";
import { useSocialMetrics } from "@/hooks/useSocialMetrics";
import { useInsightCardRealtime } from "@/hooks/useInsightCardRealtime";

interface InsightCardProps {
  card: InsightCardType;
  onViewInsights: (card: InsightCardType) => void;
}

const InsightCard = ({ card, onViewInsights }: InsightCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [cardData, setCardData] = useState<InsightCardType>(card);
  const { linkedPolls, isLoading, handleRefreshPolls } = useLinkedPolls(cardData.id, cardData.linked_poll);
  const navigate = useNavigate();
  const { canVote } = useVotePermissions();
  const { metrics, isLoading: isMetricsLoading } = useSocialMetrics(cardData.id);
  
  // Handle real-time updates to card data
  const handleCardUpdate = useCallback((updatedCard: InsightCardType) => {
    console.log('InsightCard: Received updated card data:', updatedCard);
    setCardData(updatedCard);
  }, []);
  
  // Set up real-time listener for card updates
  useInsightCardRealtime(cardData.id, handleCardUpdate);
  
  // Update polls when linkedPolls changes
  useEffect(() => {
    console.log(`InsightCard: Setting polls from linkedPolls (${linkedPolls.length}) for card ${cardData.id}`);
    setPolls(linkedPolls);
  }, [linkedPolls, cardData.id]);

  // Update local card state when prop changes
  useEffect(() => {
    setCardData(card);
  }, [card]);

  const handleViewDetails = () => {
    if (cardData.slug) {
      navigate(`/insight/${cardData.slug}`);
    } else {
      onViewInsights(cardData);
    }
  };

  // Create sharing URL based on the insight slug
  const shareUrl = cardData.slug 
    ? `${window.location.origin}/insight/${cardData.slug}`
    : window.location.href;

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border hover:shadow-lg transition-shadow">
      <div className="p-5">
        {/* Header section with trending badge positioned absolutely to prevent overflow */}
        <div className="relative mb-2">
          <CardHeader 
            category={cardData.category}
            linkedPoll={cardData.linked_poll}
            statsStatus={cardData.stats_status}
            totalPollsters={cardData.totalPollsters}
            linkedPolls={linkedPolls}
          />
          
          {/* Trending badge positioned absolutely in top-right */}
          {!isMetricsLoading && metrics && metrics.heatScore > 50 && (
            <div className="absolute top-0 right-0">
              <TrendingBadge score={metrics.heatScore} size="sm" />
            </div>
          )}
        </div>
        
        <h3 className="text-xl font-bold text-foreground mb-2">{cardData.title}</h3>
        <p className="text-muted-foreground text-sm mb-4">{cardData.description}</p>
        
        <AgreementBar percentage={cardData.agreePercentage} />
        
        {/* Linked polls section using centralized systems */}
        {(cardData.linked_poll || linkedPolls.length > 0) && (
          <LinkedPollsSection
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            linkedPolls={linkedPolls}
            isLoading={isLoading}
            onRefresh={handleRefreshPolls}
            canVote={canVote()}
          />
        )}

        {/* Social interaction section */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          <LikeButton 
            cardId={cardData.id} 
            initialLikes={metrics?.likesCount || 0} 
            size="sm" 
          />
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleViewDetails}
            className="gap-1"
          >
            <MessageSquare size={16} />
            {isMetricsLoading ? '...' : metrics?.commentsCount || 0}
          </Button>
          
          <ShareButton
            contentId={cardData.id}
            title={cardData.title}
            description={cardData.description}
            url={shareUrl}
            initialShares={metrics?.sharesCount || 0}
            size="sm"
          />
          
          <div className="grow"></div>
          
          <Button 
            variant="outline" 
            className="ml-auto"
            size="sm"
            onClick={handleViewDetails}
          >
            <FileText size={16} className="mr-1" />
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InsightCard;
